'use client';

import { useCallback, useState } from 'react';

type OptimisticBookmarkOptions = {
  id: string;
  bookmarked?: boolean;
  onBookmark?: (id: string) => Promise<void>;
  onUnbookmark?: (id: string) => Promise<void>;
};

type BookmarkSnapshot = {
  id: string;
  /** What the prop said when the flag was last taken from it. */
  fromProps?: boolean;
  bookmarked: boolean;
};

/**
 * Flips the bookmark on screen straight away and puts it back if the server refuses.
 *
 * The flag is re-read from the props whenever the question changes, or whenever the
 * prop itself changes for the same question — adjusted while rendering rather than in
 * an effect, so the right value is painted the first time round instead of one frame
 * late.
 *
 * `toggle` is undefined unless both callbacks are given, which is what call sites hand
 * to a bookmark button to leave it inert.
 */
function useOptimisticBookmark({
  id,
  bookmarked: fromProps,
  onBookmark,
  onUnbookmark,
}: OptimisticBookmarkOptions) {
  const [snapshot, setSnapshot] = useState<BookmarkSnapshot>({
    id,
    fromProps,
    bookmarked: fromProps ?? false,
  });

  if (
    snapshot.id !== id ||
    (fromProps !== undefined && fromProps !== snapshot.fromProps)
  ) {
    setSnapshot({ id, fromProps, bookmarked: fromProps ?? false });
  }

  const { bookmarked } = snapshot;

  const toggle = useCallback(async () => {
    setSnapshot((current) => ({ ...current, bookmarked: !current.bookmarked }));

    try {
      await (bookmarked ? onUnbookmark?.(id) : onBookmark?.(id));
    } catch {
      setSnapshot((current) => ({ ...current, bookmarked }));
    }
  }, [bookmarked, id, onBookmark, onUnbookmark]);

  return {
    bookmarked,
    toggle: onBookmark && onUnbookmark ? toggle : undefined,
  };
}

export { useOptimisticBookmark };
